import axios from 'axios';

// When REACT_APP_BACKEND_URL is not provided (e.g. production build where the
// API lives on the SAME host as the frontend), fall back to a RELATIVE URL.
// Otherwise the template literal becomes the literal string "undefined/api"
// and every call breaks silently on production.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('restaurant_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('restaurant_token');
      localStorage.removeItem('restaurant_data');
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/shop/')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/me'),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};

export const legalAPI = {
  getDocuments: () => api.get('/public/legal/documents'),
  getDocument: (docType) => api.get(`/public/legal/${docType}`),
  getPending: () => api.get('/legal/pending'),
  accept: () => api.post('/legal/accept'),
};

// Restaurant APIs
export const restaurantAPI = {
  updateProfile: (data) => api.put('/restaurant/profile', data),
  getBySlug: (slug) => api.get(`/restaurant/${slug}`),
  verifyDomain: (domain) => api.post('/restaurant/verify-domain', { domain }),
  // Stripe configuration (manual keys)
  getStripeConfig: () => api.get('/restaurants/me/stripe-config'),
  saveStripeConfig: (data) => api.post('/restaurants/me/stripe-config', data),
  removeStripeConfig: () => api.delete('/restaurants/me/stripe-config'),
  // Stripe Connect (Express accounts)
  createStripeConnectAccount: () => api.post('/restaurants/me/stripe-connect/create-account'),
  createStripeConnectLink: (data) => api.post('/restaurants/me/stripe-connect/create-link', data),
  getStripeConnectStatus: () => api.get('/restaurants/me/stripe-connect/status'),
  createStripeConnectDashboardLink: () => api.post('/restaurants/me/stripe-connect/dashboard-link'),
  disconnectStripeConnect: () => api.delete('/restaurants/me/stripe-connect'),
};

// Menu APIs
export const menuAPI = {
  // Categories
  getCategories: () => api.get('/menu/categories'),
  getPublicCategories: (restaurantId) => api.get(`/menu/categories/${restaurantId}/public`),
  createCategory: (data) => api.post('/menu/categories', data),
  updateCategory: (id, data) => api.put(`/menu/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/menu/categories/${id}`),
  reorderCategories: (categoryIds) => api.put('/menu/categories/reorder', { category_ids: categoryIds }),
  
  // Items
  getItems: () => api.get('/menu/items'),
  getPublicItems: (restaurantId) => api.get(`/menu/items/${restaurantId}/public`),
  createItem: (data) => api.post('/menu/items', data),
  updateItem: (id, data) => api.put(`/menu/items/${id}`, data),
  deleteItem: (id) => api.delete(`/menu/items/${id}`),
  generateImage: (id) => api.post(`/menu/items/${id}/generate-image`),

  // Ruimt de foto's op die het systeem zelf heeft toegekend. Eigen uploads
  // blijven staan. Stond eerst in aiAPI, terwijl MenuPage hem op menuAPI
  // zoekt: daar was hij dus onbereikbaar en gooide de knop een TypeError.
  cleanupMenuPhotos: (data) => api.post('/menu/photos/cleanup', data, { timeout: 120000 }),

  // Modifier groups (add-ons)
  getModifiers: () => api.get('/menu/modifiers'),
  createModifier: (data) => api.post('/menu/modifiers', data),
  updateModifier: (id, data) => api.put(`/menu/modifiers/${id}`, data),
  deleteModifier: (id) => api.delete(`/menu/modifiers/${id}`),
  getItemModifiers: (itemId) => api.get(`/public/items/${itemId}/modifiers`),
};

// Order APIs
export const orderAPI = {
  create: (data) => api.post('/orders', data),
  getAll: (status = null) => api.get('/orders', { params: { status } }),
  getHistory: (params) => api.get('/orders/history', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  update: (id, data) => api.put(`/orders/${id}`, data),
  updateStatus: (id, status, prepTime = null) => api.put(`/orders/${id}/status`, null, { params: { status, prep_time_minutes: prepTime } }),
  // zonderTerugbetaling is de bewuste ontsnapping als Stripe niet meewerkt;
  // de bestelling gaat dan dicht met de aantekening dat er nog handmatig
  // terugbetaald moet worden.
  cancelWithRefund: (id, zonderTerugbetaling = false) =>
    api.post(`/orders/${id}/cancel`, null,
             zonderTerugbetaling ? { params: { zonder_terugbetaling: true } } : undefined),
  getPrintTicket: (id) => `${API_URL}/orders/${id}/print`,
  markPrintSent: (id) => api.post(`/orders/${id}/print-sent`),
};

// Reservation APIs
export const reservationAPI = {
  create: (data) => api.post('/reservations', data),
  getAll: (status = null, date = null) => api.get('/reservations', { params: { status, date } }),
  updateStatus: (id, status) => api.put(`/reservations/${id}/status`, null, { params: { status } }),
};

// Delivery Zone APIs
export const deliveryZoneAPI = {
  getAll: () => api.get('/delivery-zones'),
  getPublic: (restaurantId) => api.get(`/delivery-zones/${restaurantId}/public`),
  check: (restaurantId, postalCode) => api.get(`/delivery-zones/check/${restaurantId}/${postalCode}`),
  create: (data) => api.post('/delivery-zones', data),
  update: (id, data) => api.put(`/delivery-zones/${id}`, data),
  delete: (id) => api.delete(`/delivery-zones/${id}`),
};

// Payment APIs
export const paymentAPI = {
  createCheckout: (orderId, originUrl) => api.post('/payments/checkout', { order_id: orderId, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Subscription APIs
export const subscriptionAPI = {
  createCheckout: (planId, billingCycle, originUrl) => api.post('/subscriptions/checkout', { 
    plan_id: planId, 
    billing_cycle: billingCycle,
    origin_url: originUrl 
  }),
  getStatus: (sessionId) => api.get(`/subscriptions/status/${sessionId}`),
  getCurrent: () => api.get('/subscriptions/current'),
};

// AI APIs
export const onboardingAPI = {
  getStatus: () => api.get('/onboarding/status'),
};

export const aiAPI = {
  generateDescription: (data) => api.post('/ai/generate-description', data),
  generateUpsell: (data) => api.post('/ai/generate-upsell', data),
  chat: (data) => api.post('/ai/chat', data),
  getChatHistory: (sessionId) => api.get(`/ai/chat/history/${sessionId}`),
  // Inzichten hebben een eigen, ingelogd endpoint: /ai/chat is publiek en
  // mag daarom geen omzetcijfers terugkrijgen. Ruimere timeout, want een
  // analyse duurt langer dan een gewone API-aanroep.
  getInsights: (data) => api.post('/ai/insights', data, { timeout: 60000 }),
  scanMenu: (data) => api.post('/ai/scan-menu', data),
};

// Marketing APIs
export const marketingAPI = {
  // QR Code — met discountCode wijst de QR naar de webshop met de code al toegepast
  getQRCode: (discountCode = null) =>
    api.get('/marketing/qr-code', discountCode ? { params: { discount_code: discountCode } } : undefined),
  
  // Discount Codes
  getDiscountCodes: () => api.get('/marketing/discount-codes'),
  createDiscountCode: (data) => api.post('/marketing/discount-codes', data),
  updateDiscountCode: (id, data) => api.put(`/marketing/discount-codes/${id}`, data),
  deleteDiscountCode: (id) => api.delete(`/marketing/discount-codes/${id}`),
  
  // Flyer Settings
  getFlyerSettings: () => api.get('/marketing/flyer-settings'),
  saveFlyerSettings: (data) => api.post('/marketing/flyer-settings', data),
  
  // Validate discount code (public) - uses query params
  validateDiscountCode: (code, restaurantId, email, phone) =>
    api.post('/orders/validate-discount', null, {
      params: { code, restaurant_id: restaurantId, customer_email: email, customer_phone: phone },
    }),

  // Automatische eerste-bestelling-korting voor een nieuwe klant (publiek)
  getAutoDiscount: (restaurantId, email, phone) =>
    api.get('/orders/auto-discount', {
      params: { restaurant_id: restaurantId, customer_email: email, customer_phone: phone },
    }),
};

// Public APIs (no auth)
export const publicAPI = {
  getAnnouncement: (slug) => api.get(`/restaurant/${slug}/announcement`),
  getAvailableSlots: (restaurantId, orderType = 'pickup') =>
    api.get(`/public/restaurants/${restaurantId}/available-slots`, { params: { order_type: orderType } }),
  // Blog (publiek, alleen gepubliceerde artikelen)
  getBlogPosts: (language = null, category = null) =>
    api.get('/public/blog/posts', { params: { language, category } }),
  getBlogPost: (language, slug) => api.get(`/public/blog/posts/${language}/${slug}`),
};

// Analytics APIs
export const analyticsAPI = {
  getOverview: () => api.get('/analytics/overview'),
};

// Translations APIs
export const translationsAPI = {
  get: (language) => api.get(`/translations/${language}`),
  getAll: () => api.get('/translations'),
};

// Templates APIs
export const templatesAPI = {
  getAll: () => api.get('/templates'),
  get: (id) => api.get(`/templates/${id}`),
};

// Seed data API
export const seedAPI = {
  seedDemoData: () => api.post('/seed-demo-data'),
};

// Create admin API instance with admin token
const adminApiInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

adminApiInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminApiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      if (window.location.pathname.includes('/admin') && !window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Admin APIs
export const adminAPI = {
  login: (email, password) => adminApiInstance.post('/admin/login', { email, password }),
  getProfile: () => adminApiInstance.get('/admin/me'),
  getStats: () => adminApiInstance.get('/admin/stats'),
  
  // Restaurant Management
  getRestaurants: (skip = 0, limit = 50) => adminApiInstance.get('/admin/restaurants', { params: { skip, limit } }),
  getRestaurantDetails: (id) => adminApiInstance.get(`/admin/restaurants/${id}`),
  createRestaurant: (data) => adminApiInstance.post('/admin/restaurants', data),
  updateRestaurant: (id, data) => adminApiInstance.put(`/admin/restaurants/${id}`, data),
  // override_reason alleen meesturen als je bewust activeert terwijl er nog
  // verplichte stappen ontbreken; de backend legt dat vast.
  toggleRestaurantStatus: (id, overrideReason = null) =>
    adminApiInstance.put(`/admin/restaurants/${id}/status`, null,
      overrideReason ? { params: { override_reason: overrideReason } } : undefined),
  getOnboardingOverview: () => adminApiInstance.get('/admin/onboarding'),
  getRestaurantOnboarding: (id) =>
    adminApiInstance.get(`/admin/restaurants/${id}/onboarding`),
  setOnboardingOwners: (id, owners) =>
    adminApiInstance.put(`/admin/restaurants/${id}/onboarding/owners`, { owners }),
  deleteRestaurant: (id) => adminApiInstance.delete(`/admin/restaurants/${id}`),
  resetRestaurantPassword: (id, newPassword) => adminApiInstance.post(`/admin/restaurants/${id}/reset-password`, null, { params: { new_password: newPassword } }),
  loginAsRestaurant: (id) => adminApiInstance.post(`/admin/restaurants/${id}/login-as`),
  
  // Orders
  getAllOrders: (skip = 0, limit = 100, status = null) => adminApiInstance.get('/admin/orders', { params: { skip, limit, status } }),
  
  // Subscription Plans
  getSubscriptionPlans: () => adminApiInstance.get('/admin/subscriptions/plans'),
  updateSubscriptionPlan: (planId, data) => adminApiInstance.put(`/admin/subscriptions/plans/${planId}`, data),
  updateRestaurantSubscription: (restaurantId, planId) => adminApiInstance.put(`/admin/restaurants/${restaurantId}/subscription`, null, { params: { plan_id: planId } }),
  
  // Subscription Revenue
  getSubscriptionRevenue: () => adminApiInstance.get('/admin/subscriptions/revenue'),
  getSubscribers: () => adminApiInstance.get('/admin/subscriptions/subscribers'),
  
  // Invoices/Facturen
  getInvoices: (skip = 0, limit = 100, status = null) => adminApiInstance.get('/admin/invoices', { params: { skip, limit, status } }),
  getInvoiceDetails: (id) => adminApiInstance.get(`/admin/invoices/${id}`),
  
  // Domains
  // Blog (platform-admin beheerd)
  // Juridische documenten en acceptaties
  getLegalDocuments: () => adminApiInstance.get('/admin/legal/documents'),
  publishLegalDocument: (data) => adminApiInstance.post('/admin/legal/documents', data),
  getRestaurantLegal: (id) => adminApiInstance.get(`/admin/restaurants/${id}/legal`),

  // E-mailadres is tevens de inlognaam: eigen endpoint met logboek en
  // hervalidatie, niet via de generieke restaurant-update.
  changeRestaurantEmail: (id, email) =>
    adminApiInstance.put(`/admin/restaurants/${id}/email`, { email }),
  resendRestaurantVerification: (id) =>
    adminApiInstance.post(`/admin/restaurants/${id}/resend-verification`),
  markRestaurantVerified: (id, note) =>
    adminApiInstance.post(`/admin/restaurants/${id}/mark-verified`, { note }),
  getRestaurantEmailLog: (id) =>
    adminApiInstance.get(`/admin/restaurants/${id}/email-log`),

  getBlogPosts: () => adminApiInstance.get('/admin/blog/posts'),
  getBlogPost: (id) => adminApiInstance.get(`/admin/blog/posts/${id}`),
  createBlogPost: (data) => adminApiInstance.post('/admin/blog/posts', data),
  updateBlogPost: (id, data) => adminApiInstance.put(`/admin/blog/posts/${id}`, data),
  deleteBlogPost: (id) => adminApiInstance.delete(`/admin/blog/posts/${id}`),

  // SEO (platform-managed, auto-generated from delivery zones)
  getSeoOverview: () => adminApiInstance.get('/admin/seo'),
  setSeoOverride: (restaurantId, data) => adminApiInstance.put(`/admin/restaurants/${restaurantId}/seo`, data),
  recomputeSeo: (restaurantId = null) => adminApiInstance.post('/admin/seo/recompute', null, restaurantId ? { params: { restaurant_id: restaurantId } } : undefined),

  getDomains: () => adminApiInstance.get('/admin/domains'),
  verifyDomain: (restaurantId) => adminApiInstance.put(`/admin/domains/${restaurantId}/verify`),
  removeDomain: (restaurantId) => adminApiInstance.delete(`/admin/domains/${restaurantId}`),
  
  // Settings
  getSettings: () => adminApiInstance.get('/admin/settings'),
  updateSettings: (data) => adminApiInstance.put('/admin/settings', data),
  
  // Audit Log
  getAuditLog: (skip = 0, limit = 100) => adminApiInstance.get('/admin/audit-log', { params: { skip, limit } }),
};

// Printer APIs
export const printerAPI = {
  getConfig: () => api.get('/restaurants/me/printer-config'),
  saveConfig: (data) => api.post('/restaurants/me/printer-config', data),
  generateKey: () => api.post('/restaurants/me/printer/generate-key'),
  printOrder: (orderId) => api.post(`/orders/${orderId}/print`),
  getPendingJobs: (apiKey) => api.get(`/print/pending/${apiKey}`),
};

// Customer APIs (aggregated unique customers + CSV export)
export const customerAPI = {
  list: () => api.get('/restaurants/me/customers'),
  exportCsvUrl: () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/restaurants/me/customers/export`,
};

export default api;
